import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Award, Loader2 } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import BadgeDisplay from "./BadgeDisplay";
import { submitQuiz } from "../lib/api";

export const QuizModal = ({ module: mod, open, onClose, onAwarded }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open || !mod) return null;

  const pick = (qIdx, optIdx) => setAnswers({ ...answers, [qIdx]: optIdx });

  const submit = async () => {
    const ordered = mod.quiz.map((_, i) => (answers[i] !== undefined ? answers[i] : -1));
    setSubmitting(true);
    try {
      const r = await submitQuiz(mod.id, ordered);
      setResult(r);
      if (r.badge && r.cadet) onAwarded && onAwarded(r.badge, r.cadet);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setAnswers({}); setResult(null); };
  const allAnswered = mod.quiz.every((_, i) => answers[i] !== undefined);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto"
        data-testid="quiz-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 16 }}
          animate={{ scale: 1, y: 0 }}
          className="eigoo-glass rounded-3xl p-6 md:p-10 w-full max-w-3xl my-8"
        >
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-yellow-300 font-display">Proof of Skill · Module {mod.numeral}</div>
              <div className="font-display font-black text-2xl md:text-3xl">{mod.title}</div>
            </div>
            <button onClick={() => { reset(); onClose(); }} className="text-white/50 hover:text-white hit-target" data-testid="close-quiz"><X /></button>
          </div>

          {!result ? (
            <div className="space-y-5">
              {mod.quiz.map((q, i) => (
                <div key={`q-${q.q}`} className="rounded-2xl bg-black/40 border border-yellow-300/15 p-4" data-testid={`quiz-q-${i}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <div className="font-display font-bold text-base md:text-lg flex-1">
                      <span className="text-yellow-300 mr-2">Q{i+1}.</span>{q.q}
                    </div>
                    <SpeakerButton text={q.q} testid={`speaker-q-${i}`} size="sm" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => (
                      <button
                        key={`${q.q}-${opt}`}
                        onClick={() => pick(i, oi)}
                        data-testid={`quiz-option-${i}-${oi}`}
                        className={`text-left px-4 py-3 rounded-2xl border transition-all font-body text-base md:text-lg ${
                          answers[i] === oi
                            ? "bg-yellow-300/15 border-yellow-300/60 text-yellow-100"
                            : "bg-black/30 border-white/10 hover:border-yellow-300/30 text-white/85"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button
                onClick={submit}
                disabled={!allAnswered || submitting}
                className={`btn-eigoo btn-gold w-full ${!allAnswered ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="submit-quiz"
              >
                {submitting ? <><Loader2 className="animate-spin" size={18} /> Grading...</> : <><Award size={20} /> Submit for Proof of Skill</>}
              </button>
            </div>
          ) : (
            <div className="text-center" data-testid="quiz-result">
              <div className="flex items-center justify-center my-4">
                <BadgeDisplay
                  badge={{ ...mod, module_id: mod.id, module_numeral: mod.numeral }}
                  size="lg"
                  earned={result.result.passed}
                />
              </div>
              <div className="font-display font-black text-3xl md:text-4xl mb-1">
                {result.result.passed ? "Proof of Skill · Earned!" : "Almost there, Cadet"}
              </div>
              <div className="text-white/70 mb-4">
                Score: <span className="text-yellow-200 font-bold">{result.result.score_pct}%</span>
                {" "}· {result.result.correct}/{result.result.total} correct
                {result.result.passed && <span className="text-green-300 font-bold"> · +120 XP · +50 coins</span>}
              </div>
              <SpeakerButton
                text={result.result.passed
                  ? `Congratulations! You earned Proof of Skill for Module ${mod.numeral}, ${mod.title}. You gained 120 experience and 50 Academy Coins.`
                  : `You scored ${result.result.score_pct} percent. Read the lessons again and try once more. You've got this, Commander.`}
                testid="speaker-quiz-result"
                size="lg"
              />
              <div className="text-left mt-5 space-y-2">
                {result.result.review.map((r, i) => (
                  <div key={r.q} className={`rounded-xl p-3 border ${r.was_correct ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}>
                    <div className="text-sm">
                      {r.was_correct ? <Check className="inline text-green-300" size={14} /> : <X className="inline text-red-300" size={14} />}
                      <span className="ml-2 text-white/85">{mod.quiz[i].q}</span>
                    </div>
                    {!r.was_correct && (
                      <div className="text-xs text-white/60 mt-1 ml-5">Correct answer: <span className="text-green-300 font-bold">{mod.quiz[i].options[r.correct_index]}</span></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={reset} className="btn-eigoo btn-ghost-gold flex-1" data-testid="quiz-retry">Retry</button>
                <button onClick={() => { reset(); onClose(); }} className="btn-eigoo btn-gold flex-1" data-testid="quiz-close">Return to Academy</button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuizModal;
